/**
 * Help text constants extracted from HMRC Self Assessment documentation
 * These provide detailed explanations for form fields to assist users
 */

export const HELP_TEXT = {
  // Personal Details
  nino: `Your National Insurance Number is a 9 digit reference in format QQ123456A. You can find it on your National Insurance card, letters from Social Security, HMRC documents, or pay slips.`,
  
  addressCorrect: `Confirm if the address shown is correct. This should match your registered address with HMRC.`,
  
  telephoneNumber: `Your contact telephone number. This will be used by HMRC if they need to contact you about your tax return.`,
  
  emailAddress: `Your email address. HMRC may use this to send you important updates about your tax return.`,
  
  companyType: `Select the option that best describes your situation: Limited Company, Partnership, or Sole Trader.`,
  
  dateOfBirth: `Your date of birth as registered with HMRC. This is used to verify your identity.`,
  
  maritalStatus: `Your marital status as of the end of the tax year. This may affect certain tax allowances and reliefs.`,

  taxpayerResidency: `HMRC determines your residency status based on whether you lived in England and Northern Ireland, Scotland or Wales for the majority of the tax year between 6 April 2024 and 5 April 2025. The amount of tax you pay will depend on your residency status.

If you think your residency status is incorrect and you change it, we may have to recalculate your tax after you file this return. System updates to residency status can take up to 3 days, but we will send you a revised tax calculation (SA302) as soon as possible.`,

  blindPersonAllowance: `You may be entitled to claim blind person's allowance.

If you or your spouse or civil partner are registered blind, the local authority will enter your name(s) on its register of blind people on the production of an ophthalmologist's certificate. If you live in Scotland or Northern Ireland, local authorities aren't obliged to keep registers of blind people, although some do. But you can claim the allowance if your eyesight, or that of your spouse or civil partner, is so bad you can't do any work for which eyesight is essential. Your eye specialist will normally certify you blind before you're entitled to this allowance.

If you were registered for the first time after 5 April 2025 you may still be entitled to claim on this Tax Return if you have evidence that your sight condition existed in the year 6 April 2024 to 5 April 2025.

If you're registered blind, choose 'Yes' from the drop-down menu otherwise, choose 'No'.`,

  studentLoanICR: `Income Contingent Repayment (ICR) Student Loans are collected by HMRC. If repayment should have begun before 6 April 2025, select 'Yes'. There are three plan types with different thresholds: Plan 1 (£24,990), Plan 2 (£27,295), and Plan 4 (£31,395). Repayments are 9% on income over these thresholds.`,
  
  studentLoanICRFull: `Income Contingent Repayment (ICR) Student Loans are collected by HMRC. You will have received a letter from the Student Loans Company (SLC) before you were due to start repaying your loan telling you the date from which any ICR loan repayments will become due.

If you have an ICR Student Loan for which repayment should have begun before 6 April 2025,(regardless of whether your income was below the threshold for Student loan repayments) do not select 'No'.

There are three income thresholds, known as plan type 1 plan type 2 and plan type 4.

The plan type 1 type threshold is £24,990. If your deductions are based on plan type 1 and you earn less than £24,990 you won't have to make any repayments.

The plan type 2 threshold is £27,295. If your deductions are based on plan type 2 and you earn less than £27,295 you won't have to make any repayments.

The plan type 4 threshold is £31,395. If your deductions are based on plan type 4 and you earn less than £31,395 you won't have to make any repayments.

Student Loan repayments are then collected at a rate of 9% on any income over these limits.

Student Loan repayments are worked out as described above along with any tax and National Insurance contributions. Self Assessment Student Loan repayments are due to be paid by 31 January following the year of assessment. This means that they will be included in your 31 January balancing payment each year. They are not included in the Self Assessment payment-on-account figures calculated during the year.`,

  studentLoanPostgraduate: `Income Contingent Postgraduate Loans are collected by HMRC. You will have received a letter from the Student Loans Company (SLC) before you were due to start repaying your loan telling you the date from which any Postgraduate loan repayments will become due.

If you have an Income Contingent Postgraduate Loan for which repayment should have begun before 6 April 2025,(regardless of whether your income was below the threshold for Postgraduate loan repayments) do not select 'No'.

The threshold for when HMRC start to collect loan repayments is £21,000. Postgraduate Loan repayments are collected at a rate of 6% on any income over the threshold.

If you change the answer to this question from 'Yes' to 'No', you will be asked to explain why you think this is different to our records below.`,

  // Employment
  wasEmployee: `Select 'Yes' if you were an employee, director, office holder, or agency worker from 6 April 2024 to 5 April 2025, or received payments/benefits from a former employer. Select 'No' if none apply.`,
  
  wasEmployeeFull: `If any of the following apply to you from 6 April 2024 to 5 April 2025 choose 'Yes':

• employee, with pay or benefits
• director, with pay or benefits
• office holder
• agency worker
• you received payments or benefits from a former employer, excluding a pension
• were resident in the UK and received an income from any foreign employment
• received a disguised remuneration loan from a third party in respect of your employer or ex-employer

If none of the above apply select 'No'.

If you received income as a sub-contractor in the construction industry, do not complete this section for that income and Construction Industry Scheme (CIS) deductions, complete the self-employment section.

For information:
• an office holder is someone such as a chairperson, secretary or treasurer of an organisation
• you should use the Employment page to report income from any foreign employment, directorship or office
• you'll need 1 Employment section, for example, for each employment, directorship and so on

Do not complete the Employment pages if you:
• held an office, but not as a director, and were only reimbursed for actual out of pocket expenses, no other payments were made to you at all
• were a company director but received no payments of any kind or benefits from that directorship
• held an office or employment but no liability to UK Income Tax arises on those earnings because you were resident or domiciled outside the UK

If any of the 3 bullets above apply to you, tell us why you are not completing an Employment page in the 'Any other information' box and give the name of the particular directorship you are not submitting a page for.

Important: If you held 2 or more offices or employments with the same employer or associated employers, and earnings from one are chargeable to UK Income Tax but the earnings from the others are not, you should complete an Employment page for each employment.`,

  // Self Employment
  wasSelfEmployed: `Select 'Yes' if you were self-employed or a subcontractor in the building industry with turnover over £1,000 in the year ending 5 April 2025. Turnover up to £1,000 is exempt, but you may still choose to report it.`,
  
  wasSelfEmployedFull: `If in the year ending 5 April 2025 you either:

• carried on a trade or vocation as a self-employed person, but not in a partnership
• were a subcontractor working in the building industry

and your turnover was more than £1,000, you should answer 'Yes' to this question.

If your turnover from all self-employments including any miscellaneous income was up to £1,000 you should answer 'No' (subject to the points below). Turnover up to £1,000 is exempt from tax and you do not need to report this on a tax return.

If your total self-employment income was no more than £1,000, you may choose to complete the self-employment section.

Also select 'Yes' if you were self-employed and your turnover was £1,000 or less if:

• your allowable expenses are higher than your turnover and you want to claim relief for the loss or carry forward a loss to be used against future profits
• you want to voluntarily pay Class 2 National Insurance contributions to build entitlement to contributory benefits like State Pension
• you're a subcontractor and want to claim back overpaid tax deducted under the Construction Industry Scheme (CIS)
• you expect your turnover to exceed £1,000 in the next tax year
• you want to preserve your record of self-employment, for example to support an application for Maternity Allowance
• you would like to claim Tax Free Childcare based on your self-employment income

Note, if you choose not to pay voluntary contributions this may affect the amount of state pension you receive and the ability to claim some benefits.

If you are a foster carer, you are classed as Self Employed and should select 'Yes' to this question.

Trading income allowance: If your turnover from all self-employments plus miscellaneous income was up to £1,000, then they are exempt from tax and do not need to be reported on a tax return. The trading income allowance of up to £1,000 can be split across more than one business and against miscellaneous income.`,

  // Partnership
  wasInPartnership: `If you carried on a trade, profession, vocation or other business in partnership in the year ended 5 April 2025, choose 'Yes' otherwise choose 'No'.`,

  // UK Property
  hasProperty: `If you received:

• rental income and other receipts from UK land and/or property
• income from letting furnished rooms in your own home
• income from letting furnished holiday accommodation in the UK or European Economic Area (EEA)
• premiums arising from leases of UK land
• an inducement to take an interest in any property for letting, a reverse premium

of over £1,000, including any income from foreign property reported on the 'Foreign section, answer 'Yes'.

Property income allowance: All income from property, including any foreign property, up to £1,000, is exempt from tax and does not need to be reported on a tax return, unless the income is from a connected party.

If your total property income was less than £1,000, you may choose to fill in the UK property section because:

• you do not want to claim the allowance as your allowable expenses are higher than your turnover and you want to be able to claim relief for the loss against future property income
• you're a non-resident landlord and want to claim back tax paid under the non-resident landlord scheme`,

  // Foreign Income
  hasForeignIncome: `If you received:

• interest and income from overseas savings
• dividends over £500 from foreign companies
• distributions and excess 'reported income' from reporting offshore funds - this is taxable income accumulating in an offshore fund that you have not yet received
• overseas pensions, including taxable lump sums from overseas schemes treated as pension income, social security benefits and royalties
• income from land and property abroad, but not furnished holiday lettings in the European Economic Area, these go in the 'UK land and property' section
• discretionary income from non-resident trusts
• income or benefits from a person abroad or a non-resident company or trust, including a UK trust that has either been, or has received income from, a non-resident trust
• gains on foreign life insurance policies or on disposals in offshore funds

answer 'Yes'.

Also select 'Yes' if you want to claim Foreign Tax Credit Relief or Special Withholding Tax on employment, self-employment or partnership income or capital gains from abroad. Foreign income from these sources should be included in the relevant sections of this tax return, not the foreign section.`,

  // Capital Gains
  hasCapitalGains: `You must report your capital gains and attach your computations if in the tax year either:

• you disposed of chargeable assets which were worth more than £50,000
• your chargeable gains, before the deduction of any losses, are more than £3,000 (the Annual Exempt Amount)
• you have gains in an earlier year taxable in this period
• you want to claim an allowable capital loss or make any other capital gains claim or election for the year

In working out if the assets you disposed of were worth more than £50,000 use the market value of any assets you gave away or sold for less than full value and ignore disposals of:

• exempt assets such as private cars, shares held within Individual Savings Accounts
• assets to your spouse or civil partner, if you were living together at some time during the tax year
• your own home where:
  - it's been your only home during your ownership and was not used for any other purposes, for example, in your business
  - the house has been used as your home throughout your ownership, but you can ignore the last 18 months of ownership
  - the garden and grounds disposed of at the same time do not exceed half a hectare

In working out your total chargeable gains include any gains attributed to you, for example, because you're a settlor or beneficiary of a trust, or in certain cases where you're a member of a non-resident company.`,

  // Interest/Savings
  hasInterest: `If you received, for example, any UK bank, building society, unit trust interest, select 'Yes', otherwise select 'No'.

Also select 'Yes' instead of completing the 'Foreign' section of the return, if your only foreign income was either or both:

• untaxed foreign interest up to £2,000
• taxed foreign dividends up to £500`,

  // Dividends
  hasDividends: `If you received any dividends, select 'Yes', otherwise select 'No'.`,
  
  hasDividendsFull: `If you received any dividends, select 'Yes', otherwise select 'No'.

Also select 'Yes' instead of completing the 'Foreign' section of the return, if your only foreign income was either or both:

• taxed foreign dividends up to £500
• untaxed foreign interest up to £2,000`,

  // UK Pensions
  hasPensions: `If you received any UK pensions, annuities or state benefits, select 'Yes', otherwise select 'No'.`,

  // Child Benefit
  hasChildBenefit: `You may have to pay the High Income Child Benefit Charge if your adjusted net income for the tax year was more than £60,000.

Answer 'Yes' to this question if:

• you or your partner got Child Benefit payments during the 6 April 2024 to 5 April 2025 tax year, and the payments had not stopped before 6 April 2024
• someone other than you or your partner got Child Benefit payments for a child or children who lived with you during the 6 April 2024 to 5 April 2025 tax year

Important - If you made a new claim for child benefit between 6 April and 7 July 2024, you may have received an amount of child benefit backdated to the 2023-24 tax year. The backdated amount is deemed as though it was received in the 2024-25 tax year and must be declared in this tax return.

If you found it more beneficial to report the backdated child benefit in the 2023 to 2024 tax year rather than the 2024 to 2025 tax year, the backdated amount does not need to be declared in this return; instead, you can include it in your 2023 to 2024 tax return.

Otherwise, if the above does not apply answer 'No'.`,

  // Other Income
  hasOtherIncome: `If you received any of the following income, select 'Yes':

• miscellaneous income - for example, from casual earnings, commission or freelance income, not exempted by the trading income allowance
• business receipts where your business has ceased
• Property Income Distributions (PIDs) from:
  - Real Estate Investment Trusts (REITs)
  - Property Authorised Investment Funds (PAIFs)
• payments from a personal insurance policy for sickness or disability benefits
• income from unauthorised unit trusts
• taxable annual payments
• profits from certificates of deposit
• non-cash benefits you received for being a former employee
• the following authorised payments from an overseas pension scheme:
  - the taxable part of an 'uncrystallised funds pension lump sum'
  - a winding-up lump sum or a trivial commutation lump sum
  - payments after age 75 as a serious ill-health lump sum, from 16 September 2016, or authorised lump sum death benefit

Receipts from self-employment and certain miscellaneous income of £1,000 or less are exempt from tax and do not need to be reported on a tax return. If the total receipts combined are more than £1,000, the 'self-employment' section must be completed to report the self-employment income and the miscellaneous income must be reported in 'Other UK income' section ('Any other income').`,

  // Pension Contributions
  hasPensionContributions: `Payments deducted from your pay before it's taxed, the net pay arrangement: If your payments to your employer's occupational pension scheme, or any associated Additional Voluntary Contributions, are deducted from your pay before it's taxed you will already have received your tax relief. Do not enter anything on this return.

If you make contributions towards a personal pension or retirement annuity, or you made payments to an employer's registered pension scheme and no tax relief was given at the time of payment, select 'Yes', otherwise select 'No'.

Personal Pension: If you're a UK individual who is building up benefits in a registered pension scheme, you can usually have tax relief on your payments, your contributions or those of someone else, other than your employer, on your behalf, into that scheme up to a maximum of £2,880 net (£3,600 gross).

You will only be able to receive tax relief for more than this if you either:
• have taxable UK earnings, such as employment income or profits from self-employment
• be resident in the UK for some time during the tax year
• have been UK resident at some time in the 5 preceding years and when you joined the pension scheme
• have earnings from overseas Crown employment, taxable in the UK, or your spouse does`,

  // Charitable Donations
  hasCharitableDonations: `If you gave a Gift Aid donation to charity or to a Community Amateur Sports Club or a gift of qualifying shares, securities, land or buildings to a charity select 'Yes', otherwise, select 'No'.`,

  // Married Couple's Allowance
  hasMarriedCouplesAllowance: `If both you and your spouse (or civil partner) were born after 5 April 1935, you cannot claim Married Couple's Allowance. Please answer 'No' to this question.

Married Couple's Allowance can only be claimed if either you, your spouse or civil partner, were born before 6 April 1935, and you are either:

• a married man or married woman who married before 5 December 2005
• a married man, married woman or civil partner who married or formed a civil partnership on or after 5 December 2005

If you want to claim Married Couple's Allowance, select 'Yes' otherwise, select 'No'.

The allowance is made up of 2 amounts a minimum amount, worth up to £427, plus an income-related amount dependent on the income of the husband, for marriages before 5 December 2005, or the person with the higher income, for marriages and civil partnerships formed on or after 5 December 2005, worth up to £681.

If you've already claimed Married Couple's Allowance in the tax year from 6 April 2020 to 5 April 2021 and you remarry or form a civil partnership, it's usually better to continue to claim for your former spouse or civil partner.`,

  // Other Reliefs
  hasOtherReliefs: `Select 'Yes' if you want to claim other reliefs like Seafarers' Earnings Deduction, Foreign Tax Credit Relief, EIS/VCT subscriptions, or maintenance payments.`,
  
  hasOtherReliefsFull: `Select 'Yes' if you want to claim any other reliefs or deductions, such as:

• Seafarers' Earnings Deduction
• Foreign earnings not taxable in the UK
• Foreign tax for which tax credit relief is not claimed elsewhere
• Exempt employers' contributions to an overseas pension scheme
• UK patent royalty payments made
• Subscriptions for Venture Capital Trust shares
• Subscriptions for shares under the Enterprise Investment Scheme
• Community Investment Tax Relief
• Annual payments made
• Qualifying loan interest payable in the year
• Post-cessation trade relief and certain other losses
• Maintenance payments
• Payments to a trade union, for example, for death benefits
• Relief on a qualifying distribution on the redemption of bonus shares or securities
• Subscriptions for shares under the Seed Enterprise Investment Scheme
• Non-deductible loan interest from investments into property letting partnerships

Otherwise select 'No'.`,

  // Additional help text for form fields
  firstName: `Your legal first name as it appears on official documents.`,
  middleName: `Your middle name(s) if applicable.`,
  lastName: `Your legal surname or family name.`,
  addressLine1: `The first line of your address, typically your house number and street name.`,
  addressLine2: `Additional address information such as apartment number, building name, etc.`,
  addressLine3: `City or town name.`,
  addressLine4: `County or region.`,
  postcode: `Your UK postcode in the format SW1A 1AA.`,
  country: `Your country of residence.`,
  employerName: `The name of your employer or company you worked for.`,
  payeReference: `Your employer's PAYE reference number, usually found on your payslip.`,
  payrollId: `Your payroll ID or employee number.`,
  totalPay: `Your total gross pay for the tax year before deductions.`,
  totalTax: `Total tax deducted from your pay during the tax year.`,
  startDate: `The date you started this employment.`,
  cessationDate: `The date you left this employment, if applicable.`,
  payFrequency: `How often you were paid - weekly or monthly.`,
  occupationalPension: `Whether you contributed to an occupational pension scheme through this employment.`,
  turnover: `Total sales or income from your business before expenses.`,
  otherIncome: `Any other income from self-employment not included in turnover.`,
  costOfGoods: `Cost of goods or materials purchased for resale or use in your business.`,
  staffCosts: `Wages, salaries, and other costs for employees.`,
  travelCosts: `Business travel expenses including fuel, parking, and public transport.`,
  premisesCosts: `Rent, rates, utilities, and other costs for business premises.`,
  rentIncome: `Income received from renting out property.`,
  premiums: `Premiums received from granting leases.`,
  premisesRunningCosts: `Costs of running rental property such as insurance, repairs, and management fees.`,
  repairsMaintenance: `Costs for repairs and maintenance of rental property.`,
  legalManagementCosts: `Legal and professional fees related to property rental.`,
  ukDividends: `Dividends received from UK companies.`,
  otherUkDividends: `Other UK dividend income not included above.`,
  giftAidDonations: `Donations made using Gift Aid. Enter the amount you actually gave - do not include basic rate tax.`,
  oneOffGiftAid: `One-off Gift Aid donations made during the tax year.`,
  qualifyingShares: `Value of qualifying shares, securities, land or buildings given to charity.`,
}
